import React, { useEffect, useState } from 'react';
import { validateHost, getHostErrorMessage } from '../../utils/hostValidation';

/**
 * HostValidator Component
 * 
 * Validates that the current host is allowed to access the wrapped routes.
 * Shows a 500-style error page if the host is invalid.
 * Renders children if the host is valid.
 */
const HostValidator = ({ children }) => {
    const [validationResult, setValidationResult] = useState(null);

    useEffect(() => {
        const result = validateHost();
        setValidationResult(result);
    }, []);

    // Still validating
    if (!validationResult) {
        return null;
    }

    // Show error if host is invalid
    if (validationResult.shouldShowError) {
        return (
            <div style={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)',
                fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif",
                padding: '24px',
            }}>
                <div style={{
                    background: 'rgba(255,255,255,0.06)',
                    backdropFilter: 'blur(24px)',
                    WebkitBackdropFilter: 'blur(24px)',
                    borderRadius: '28px',
                    padding: '52px 48px',
                    maxWidth: '560px',
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '28px',
                    border: '1px solid rgba(255,255,255,0.1)',
                    boxShadow: '0 32px 80px rgba(0,0,0,0.3)',
                    textAlign: 'center',
                }}>
                    {/* Error Icon */}
                    <div style={{
                        width: '80px',
                        height: '80px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, rgba(239,68,68,0.15), rgba(220,38,38,0.15))',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid rgba(239,68,68,0.25)',
                    }}>
                        <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10" />
                            <line x1="12" y1="8" x2="12" y2="12" />
                            <line x1="12" y1="16" x2="12.01" y2="16" />
                        </svg>
                    </div>

                    {/* Error Title */}
                    <div>
                        <h1 style={{
                            fontSize: '28px',
                            fontWeight: '800',
                            color: 'white',
                            margin: '0 0 10px',
                            letterSpacing: '-0.02em',
                        }}>
                            Access Denied
                        </h1>
                        <p style={{
                            fontSize: '15px',
                            color: 'rgba(255,255,255,0.55)',
                            margin: 0,
                            lineHeight: '1.6',
                        }}>
                            {getHostErrorMessage(window.location.hostname)}
                        </p>
                    </div>

                    {/* Details Box */}
                    <div style={{
                        background: 'rgba(255,255,255,0.04)',
                        borderRadius: '16px',
                        padding: '20px 24px',
                        width: '100%',
                        border: '1px solid rgba(255,255,255,0.06)',
                    }}>
                        <p style={{
                            fontSize: '13px',
                            fontWeight: '600',
                            color: 'rgba(255,255,255,0.7)',
                            margin: '0 0 12px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                        }}>Authorized Domains:</p>
                        <ul style={{
                            listStyle: 'none',
                            padding: 0,
                            margin: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '10px',
                        }}>
                            <li style={{
                                fontSize: '14px',
                                color: 'rgba(255,255,255,0.5)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                            }}>
                                <span style={{
                                    width: '5px',
                                    height: '5px',
                                    borderRadius: '50%',
                                    background: 'rgba(34,197,94,0.6)',
                                    flexShrink: 0,
                                }} />
                                app.sayify.pro
                            </li>
                        </ul>
                    </div>

                    {/* Additional Info */}
                    <div style={{
                        background: 'rgba(239,68,68,0.08)',
                        borderRadius: '12px',
                        padding: '16px 20px',
                        width: '100%',
                        border: '1px solid rgba(239,68,68,0.15)',
                    }}>
                        <p style={{
                            fontSize: '13px',
                            color: 'rgba(255,255,255,0.65)',
                            margin: 0,
                            lineHeight: '1.5',
                        }}>
                            <strong style={{ color: 'rgba(239,68,68,0.9)' }}>Error Code:</strong> 500 - Invalid Host
                        </p>
                    </div>

                    <p style={{
                        fontSize: '13px',
                        color: 'rgba(255,255,255,0.35)',
                        margin: 0,
                    }}>
                        If you believe this is an error, please contact support.
                    </p>
                </div>

                {/* Powered By */}
                <p style={{
                    marginTop: '32px',
                    fontSize: '12px',
                    color: 'rgba(255,255,255,0.25)',
                }}>
                    Powered by <a href="https://sayify.pro" style={{ color: 'rgba(255,255,255,0.4)', textDecoration: 'none' }}>Sayify.pro</a>
                </p>
            </div>
        );
    }

    // Host is valid, render children
    return <>{children}</>;
};

export default HostValidator;
